
import { useState } from 'react';
import { volunteerGallery } from '../../../mocks/volunteerData';

export default function GallerySection() {
  const [showAll, setShowAll] = useState(false);
  const [selectedImage, setSelectedImage] = useState<typeof volunteerGallery[0] | null>(null);

  const displayedGallery = showAll ? volunteerGallery : volunteerGallery.slice(0, 6);

  return (
    <section className="py-12 md:py-20 bg-[#F9F9F9]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-2xl md:text-4xl font-bold text-[#1E1E1E] mb-2 md:mb-4">
            往期风采
          </h2>
          <p className="text-sm md:text-lg text-[#6C6C6C]">
            记录每一个温暖瞬间，见证志愿者的奉献与成长
          </p>
        </div>

        {/* 移动端：上面一个大的横版，下面两个小的并排 */}
        <div className="md:hidden space-y-3">
          {displayedGallery.reduce((acc: JSX.Element[], item, index) => {
            // 每3个为一组：1个大的 + 2个小的
            const groupIndex = Math.floor(index / 3);
            const positionInGroup = index % 3;
            
            if (positionInGroup === 0) {
              // 大图（横版）
              acc.push(
                <div
                  key={`large-${item.id}`}
                  className="group cursor-pointer"
                  onClick={() => setSelectedImage(item)}
                >
                  <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                    <div className="relative overflow-hidden h-[160px]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-[#1E1E1E] mb-1 group-hover:text-[#2A7A62] transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                      <div className="flex items-center text-[9px] text-[#6C6C6C]">
                        <i className="ri-calendar-line mr-1 text-xs"></i>
                        {item.date}
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else if (positionInGroup === 1) {
              // 开始两个小图的容器
              const nextItem = displayedGallery[index + 1];
              acc.push(
                <div key={`small-group-${groupIndex}`} className="grid grid-cols-2 gap-3">
                  {/* 第一个小图 */}
                  <div
                    className="group cursor-pointer"
                    onClick={() => setSelectedImage(item)}
                  >
                    <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                      <div className="relative overflow-hidden h-[100px]">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-2">
                        <h3 className="text-[10px] font-semibold text-[#1E1E1E] mb-0.5 group-hover:text-[#2A7A62] transition-colors line-clamp-1">
                          {item.title}
                        </h3>
                        <div className="flex items-center text-[8px] text-[#6C6C6C]">
                          <i className="ri-calendar-line mr-0.5 text-[9px]"></i>
                          {item.date}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 第二个小图 */}
                  {nextItem && (
                    <div
                      className="group cursor-pointer"
                      onClick={() => setSelectedImage(nextItem)}
                    >
                      <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                        <div className="relative overflow-hidden h-[100px]">
                          <img
                            src={nextItem.image}
                            alt={nextItem.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-2">
                          <h3 className="text-[10px] font-semibold text-[#1E1E1E] mb-0.5 group-hover:text-[#2A7A62] transition-colors line-clamp-1">
                            {nextItem.title}
                          </h3>
                          <div className="flex items-center text-[8px] text-[#6C6C6C]">
                            <i className="ri-calendar-line mr-0.5 text-[9px]"></i>
                            {nextItem.date}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            // positionInGroup === 2 的情况已经在 positionInGroup === 1 时处理了
            return acc;
          }, [])}
        </div>

        {/* 桌面端：三列网格布局，图片拉长填满 */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {displayedGallery.map((item) => (
            <div
              key={item.id}
              className="group cursor-pointer"
              onClick={() => setSelectedImage(item)}
            >
              <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 h-full">
                <div className="relative overflow-hidden h-[280px]">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#1E1E1E] mb-2 group-hover:text-[#2A7A62] transition-colors">
                    {item.title}
                  </h3>
                  <div className="flex items-center text-sm text-[#6C6C6C]">
                    <i className="ri-calendar-line mr-2"></i>
                    {item.date}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-6 md:mt-12">
          <button 
            onClick={() => setShowAll(!showAll)}
            className="px-6 md:px-8 py-2 md:py-3 border-2 border-[#2A7A62] text-[#2A7A62] rounded-lg text-sm md:text-base font-semibold hover:bg-[#2A7A62] hover:text-white transition-all whitespace-nowrap"
          >
            {showAll ? '收起' : '查看更多精彩瞬间'}
          </button>
        </div>
      </div>

      {/* 图片详情弹窗 */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-3 right-3 w-8 h-8 md:w-10 md:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-xl md:text-2xl text-[#1E1E1E]"></i>
              </button>
            </div>
            <div className="p-4 md:p-6">
              <h3 className="text-lg md:text-2xl font-bold text-[#1E1E1E] mb-2 md:mb-3">
                {selectedImage.title}
              </h3>
              <div className="flex items-center text-sm md:text-base text-[#6C6C6C]">
                <i className="ri-calendar-line mr-2"></i>
                {selectedImage.date}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
